// Mobile-Optimized UI Components
// Provides mobile-first user experience with touch-friendly interactions
// Last updated: January 2025

import React, { useState, useEffect } from 'react';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css';

// Mobile-optimized InfoTooltip
const MobileInfoTooltip = ({ children, content }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const triggerRef = React.useRef(null);

    useEffect(() => {
        if (!triggerRef.current) return;
        if (triggerRef.current._tippy) triggerRef.current._tippy.destroy();
        const instance = tippy(triggerRef.current, {
            content: content || 'Help text coming soon.',
            allowHTML: false,
            maxWidth: window.innerWidth < 768 ? 300 : 420,
            theme: 'light',
            placement: window.innerWidth < 768 ? 'top' : 'right',
            interactive: true,
            delay: [100, 100],
            touch: true,
            touchHold: true,
            appendTo: () => document.body,
        });
        return () => instance.destroy();
    }, [content]);

    return (
        <span 
            ref={triggerRef} 
            className="inline-flex items-center cursor-help"
            onTouchStart={() => setShowTooltip(true)}
            onTouchEnd={() => setShowTooltip(false)}
        >
            {children}
            <span className="ml-1 inline-block text-blue-600 text-xs" 
                  style={{ 
                      width: window.innerWidth < 768 ? 20 : 16, 
                      height: window.innerWidth < 768 ? 20 : 16,
                      lineHeight: window.innerWidth < 768 ? '20px' : '16px',
                      textAlign: 'center',
                      borderRadius: window.innerWidth < 768 ? 10 : 8,
                      border: '1px solid #2563eb',
                      fontWeight: 700 
                  }}>?</span>
        </span>
    );
};

// Mobile-optimized Number Input
const MobileNumberInput = ({ 
    value, 
    onChange, 
    placeholder = "0", 
    step = 1,
    min = 0,
    max = 999999999,
    className = "",
    label = "",
    tooltip = "",
    ...props 
}) => {
    const [isFocused, setIsFocused] = useState(false);

    return (
        <div className="w-full">
            {label && (
                <MobileInfoTooltip content={tooltip}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {label}
                    </label>
                </MobileInfoTooltip>
            )}
            <input
                type="number"
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder={placeholder}
                step={step}
                min={min}
                max={max}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    isFocused 
                        ? 'border-blue-500 ring-blue-200' 
                        : 'border-gray-300 hover:border-gray-400'
                } ${className}`}
                style={{
                    fontSize: window.innerWidth < 768 ? '16px' : '14px', // Prevent zoom on iOS
                    minHeight: window.innerWidth < 768 ? '48px' : '40px',
                    touchAction: 'manipulation'
                }}
                {...props}
            />
        </div>
    );
};

// Mobile-optimized Select Dropdown
const MobileSelect = ({ 
    value, 
    onChange, 
    options = [], 
    className = "",
    label = "",
    tooltip = "",
    ...props 
}) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="w-full relative">
            {label && (
                <MobileInfoTooltip content={tooltip}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {label}
                    </label>
                </MobileInfoTooltip>
            )}
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
                style={{
                    fontSize: window.innerWidth < 768 ? '16px' : '14px',
                    minHeight: window.innerWidth < 768 ? '48px' : '40px',
                    appearance: 'none',
                    backgroundImage: 'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
                    backgroundPosition: 'right 12px center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '16px'
                }}
                {...props}
            >
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

// Mobile-optimized Button
const MobileButton = ({ 
    children, 
    onClick, 
    variant = "primary",
    size = "md",
    className = "",
    disabled = false,
    loading = false,
    ...props 
}) => {
    const baseClasses = "font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 active:scale-95";
    
    const variantClasses = {
        primary: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
        secondary: "bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500",
        success: "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500",
        danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500",
        outline: "border-2 border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500"
    };
    
    const sizeClasses = {
        sm: "px-3 py-2 text-sm",
        md: "px-4 py-3 text-base",
        lg: "px-6 py-4 text-lg"
    };
    
    const mobileSizeClasses = window.innerWidth < 768 ? {
        sm: "px-4 py-3 text-base min-h-[44px]",
        md: "px-6 py-4 text-lg min-h-[48px]",
        lg: "px-8 py-5 text-xl min-h-[52px]"
    } : sizeClasses;

    return (
        <button
            onClick={onClick}
            disabled={disabled || loading}
            className={`
                ${baseClasses} 
                ${variantClasses[variant]} 
                ${mobileSizeClasses[size]}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${loading ? 'relative' : ''}
                ${className}
            `}
            style={{
                touchAction: 'manipulation',
                minHeight: window.innerWidth < 768 ? '44px' : 'auto'
            }}
            {...props}
        >
            {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                </div>
            )}
            <span className={loading ? 'opacity-0' : ''}>{children}</span>
        </button>
    );
};

// Mobile-optimized Card
const MobileCard = ({ 
    children, 
    className = "",
    padding = "default",
    ...props 
}) => {
    const paddingClasses = {
        sm: "p-3",
        default: "p-4",
        lg: "p-6"
    };
    
    const mobilePaddingClasses = window.innerWidth < 768 ? {
        sm: "p-4",
        default: "p-6",
        lg: "p-8"
    } : paddingClasses;

    return (
        <div 
            className={`bg-white rounded-lg shadow-sm border border-gray-200 ${mobilePaddingClasses[padding]} ${className}`}
            style={{
                touchAction: 'pan-y' // Allow vertical scrolling
            }}
            {...props}
        >
            {children}
        </div>
    );
};

// Mobile-optimized Grid
const MobileGrid = ({ 
    children, 
    cols = 1,
    gap = "default",
    className = "",
    ...props 
}) => {
    const gapClasses = {
        sm: "gap-2",
        default: "gap-4",
        lg: "gap-6"
    };
    
    const mobileGapClasses = window.innerWidth < 768 ? {
        sm: "gap-3",
        default: "gap-4",
        lg: "gap-6"
    } : gapClasses;

    const gridCols = window.innerWidth < 768 ? 1 : cols; // Force single column on mobile

    return (
        <div 
            className={`grid grid-cols-${gridCols} ${mobileGapClasses[gap]} ${className}`}
            {...props}
        >
            {children}
        </div>
    );
};

// Mobile-optimized Modal
const MobileModal = ({ 
    isOpen, 
    onClose, 
    title, 
    children, 
    className = "",
    ...props 
}) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black bg-opacity-50">
            <div 
                className={`
                    bg-white rounded-t-lg sm:rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto
                    ${className}
                `}
                style={{
                    maxHeight: window.innerWidth < 768 ? '85vh' : '90vh'
                }}
                {...props}
            >
                {title && (
                    <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full"
                            style={{ minWidth: '44px', minHeight: '44px' }}
                        >
                            ✕
                        </button>
                    </div>
                )}
                <div className="p-4">
                    {children}
                </div>
            </div>
        </div>
    );
};

// Mobile-optimized Tab Navigation
const MobileTabs = ({ 
    tabs = [], 
    activeTab, 
    onTabChange, 
    className = "",
    ...props 
}) => {
    return (
        <div className={`w-full ${className}`} {...props}>
            <div className="flex overflow-x-auto scrollbar-hide border-b border-gray-200">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => onTabChange(tab.id)}
                        className={`
                            flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors
                            ${activeTab === tab.id 
                                ? 'border-blue-500 text-blue-600' 
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }
                        `}
                        style={{
                            minHeight: '48px',
                            touchAction: 'manipulation'
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        </div>
    );
};

// Mobile-optimized Form Section
const MobileFormSection = ({ 
    title, 
    children, 
    collapsible = false,
    defaultCollapsed = false,
    className = "",
    ...props 
}) => {
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

    return (
        <MobileCard className={`${className}`} {...props}>
            <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
                style={{ minHeight: '44px' }}
            >
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                {collapsible && (
                    <span className="text-gray-400">
                        {isCollapsed ? '▼' : '▲'}
                    </span>
                )}
            </div>
            {(!collapsible || !isCollapsed) && (
                <div className="mt-4 space-y-4">
                    {children}
                </div>
            )}
        </MobileCard>
    );
};

// Export all components
export {
    MobileInfoTooltip,
    MobileNumberInput,
    MobileSelect,
    MobileButton,
    MobileCard,
    MobileGrid,
    MobileModal,
    MobileTabs,
    MobileFormSection
};

// Hook for mobile detection
export const useMobileDetection = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
};

// Hook for touch detection
export const useTouchDetection = () => {
    const [isTouch, setIsTouch] = useState('ontouchstart' in window);

    return isTouch;
};

export default {
    MobileInfoTooltip,
    MobileNumberInput,
    MobileSelect,
    MobileButton,
    MobileCard,
    MobileGrid,
    MobileModal,
    MobileTabs,
    MobileFormSection,
    useMobileDetection,
    useTouchDetection
};
